/**
 * Kapitel „Kanäle & Werbeflächen" der Sektion „Anfos & Umsatz" (/auswertungen/kanaele).
 * Plan .claude/plans/2026-10-20-p3-kanaele-werbeflaechen.md (P3), Umbau nach dem KPI-Review
 * .claude/plans/2026-10-23-p3-kpi-review-umbau.md (Spezifikation vorschlag_v3, Entscheidungen Till 23.10.2026).
 * Backend: /api/auswertungen/kanaele/*.
 *
 * Ein Zeitraum für das ganze Kapitel (3 / 6 / 12 Monate, Standard 6); nur der Verlauf je Monat zeigt immer volle
 * Monate. Dazu die Vergleichsbasis der Veränderung (E6: Vorjahr | Vorzeitraum, Standard Vorjahr, je Browser
 * gespeichert) für die Kacheln und die Kanal-Tabelle. Einladungs- und Bestell-Link-Karte haben Werbeflächen- und
 * Quellen-Karte abgelöst.
 *
 * Persona-Umbau D (Plan 2026-10-28-persona-umbau-paket-1-2, 28.10.2026): Richtung (▲/▼ gegen die gewählte Basis) an allen
 * drei Kacheln, die Bezahlquote in Prozentpunkten mit der ▲/▼-Regel von P2 (D1); neue erste Karte „Was 1.000 Kontakte je
 * Werbefläche bringen" (D2); Reihenfolge Rang → Kanäle → Verlauf → Einladungen → Bestell-Links (E3 dahinter unverändert);
 * die Blog-Karte entfällt (D4, P5 „Je Blog" bleibt); Wörterzeile ans Seitenende (D5).
 */
import { FC, ReactNode, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChapterPage from '../../../components/auswertungen/ChapterPage';
import Explainer, { ExplainerSection } from '../../../components/Explainer';
import InfoTip from '../../../components/InfoTip';
import { fetchKanaele, fetchWerbeflaechen, KanaeleAntwort, KanalFensterMonate, VergleichsBasis } from '../../../helpers/auswertungenData';
import { formatEur, formatPct, LEITFRAGEN, TERMS } from '../../../helpers/kpiDefinitions';
import { int, useLaden } from '../webinare-produkte/shared';
import BestellLinksKarte from './BestellLinksKarte';
import { ppText, richtung } from './befund';
import EinladungenKarte from './EinladungenKarte';
import KanalKarte from './KanalKarte';
import { fetchDmHaelften } from './personaDaten';
import RangKarte from './RangKarte';
import RueckblickKarte from './RueckblickKarte';
import VerlaufKarte from './VerlaufKarte';
import Woerterzeile from './Woerterzeile';
import {
  BasisWahl,
  basisBegriff,
  basisFenster,
  FensterWahl,
  fensterSatz,
  fest,
  formatDelta,
  Kachel,
  reifBasisFenster,
  reifSatz,
  tEuro,
  useLadenFenster,
  useVergleichsbasis,
  veraenderungTip,
} from './shared';

/** Leitfrage — wortgleich auf der Übersicht (`scenes/auswertungen/index.tsx`, KAPITEL). */
const KANAELE_FRAGE = LEITFRAGEN.kanaele;

const EXPLAINER: ExplainerSection[] = [
  {
    label: 'Datengrundlage',
    body: 'PL-X-Bestellungen mit Zahlungen, die Kennung im Bestell-Link, die Webinar-Anmeldungen und die Empfängerzahlen der Versände (Webinar-Mailings, Newsletter-Ausgaben, Direkt-Mailings, Welcome, Web Push und die Versandlisten der neueren Werbeplätze). Gezählt werden dieselben Anfos wie überall in der Sektion (ohne Stornos und Testbestellungen).',
  },
  {
    label: 'Berechnung',
    // B63/B75 (26.10.2026): der Vorzeitraum endet nicht am Datenstand; „die Webinar-Auswertungen" war die alte Seite, die anders rechnet.
    body: 'Der Kanal kommt aus der Kennung im Bestell-Link — jede Anfo hat genau einen Kanal. Die Veränderung vergleicht den erwarteten Wert mit dem Vorjahreszeitraum oder dem gleich langen Zeitraum direkt davor (Umschalter oben, bei 12 Monaten immer das Vorjahr). Der Zeitraum endet am Datenstand von PL-X; das Vorjahr ein Jahr früher zur selben Uhrzeit, der Vorzeitraum direkt vor dem Zeitraum. Die Werbeflächen rechnen je 1.000 Kontakte: Webinar-Einladungen über die erste Anmeldung einer Person je Webinar, direkte Bestell-Links über die Kennung im Link. Versände der letzten 14 Tage zählen dort noch nicht. Show-up wie auf „Webinare & Produkte“: nur vor Beginn Angemeldete; Webinare mit Tracking-Warnung, bekannter Messlücke oder übernommenen Anmeldungen zählen dabei nicht mit. Seit Oktober misst der eigene Webinarraum die Teilnahme genauer. Anmeldungen nach Beginn werden seit 25.08.2026 erfasst. Bezahlquoten nur über Anfos ab 60 Tagen; gegen den Vergleichszeitraum in Prozentpunkten, ▲/▼ nur, wo ein Test den Unterschied vom Zufall trennt (wie auf „Umsatz & Bezahlqualität“).',
  },
  {
    label: 'Aussage',
    body: 'Woher die Anfos kommen, was sie erwarten lassen, ob ein Kanal wächst oder schrumpft, und welche Werbefläche je 1.000 Kontakte am meisten bringt — als Grundlage, wohin der nächste Versand geht.',
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: 'Euro netto. Kanäle und Flächen über den erwarteten Wert vergleichen, nicht über „bezahlt“: bezahlt wächst mit dem Alter, junge Flächen sähen sonst schlechter aus. Werte mit wenigen Fällen stehen grau und beim Sortieren am Ende.',
  },
];

/** Farbe der Richtung: ▲ grün, ▼ rot (wie `AbweichungMarke`); grau, wenn der Vergleich auf wenigen Anfos steht. */
const PFEIL_FARBE: Record<'▲' | '▼', string> = { '▲': '#2e7d32', '▼': '#c62828' };

/**
 * Richtungszeile einer Kopf-Kachel (D1): „▼ −21 % ggü. Vorjahr (29.04.–27.10.2025)" — das Fenster steht im Etikett
 * (Leitregel 2). `pfeil` explizit (Bezahlquote: nur mit Test-Marke), sonst aus dem Vorzeichen.
 */
const Richtung: FC<{ wert: ReactNode; pfeil: '▲' | '▼' | null; grau?: boolean; etikett: ReactNode; tip: string }> = ({ wert, pfeil, grau, etikett, tip }) => (
  <Box component="span" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 0.25, color: grau ? 'text.disabled' : 'text.primary' }}>
    {pfeil && (
      <Box component="span" aria-label={pfeil === '▲' ? 'gestiegen' : 'gesunken'} sx={{ mr: 0.5, color: grau ? 'inherit' : PFEIL_FARBE[pfeil] }}>
        {pfeil}
      </Box>
    )}
    <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
      {wert}
    </Box>
    {etikett}
    <InfoTip text={tip} />
  </Box>
);

interface KachelKontext {
  k: KanaeleAntwort;
  basis: VergleichsBasis;
  monate: KanalFensterMonate;
}

/** Kachel „Anfos": Anzahl, Richtung der Anfos (ohne vorläufige) gegen die gewählte Basis. */
const AnfosKachel: FC<KachelKontext> = ({ k, basis, monate }) => {
  const v = basis === 'vorjahr' ? k.total.veraenderung.vorjahr : k.total.veraenderung.vorher;
  const begriff = basisBegriff(basis);
  const jetzt = k.total.veraenderung.jetzt.anfos;
  return (
    <Kachel
      wert={int(k.total.anfos)}
      label={`${TERMS.anfos.label} in ${monate} Monaten`}
      tip={TERMS.anfos.tip}
      sub={
        <Richtung
          wert={v.neu ? 'neu' : formatDelta(v.delta_anfos)}
          pfeil={v.neu ? null : richtung(v.delta_anfos)}
          grau={v.grau_grund !== null}
          etikett={`${begriff.short} (${basisFenster(k.vergleich, basis)})`}
          tip={`Anfos ohne vorläufig ergänzte: ${int(jetzt)} gegen ${int(v.basis.anfos)}. ${fensterSatz(k.vergleich, basis)}`}
        />
      }
    />
  );
};

/** Kachel „Erwarteter Wert": Summe, Ø je Anfo und die Veränderung gegen die gewählte Basis (Zerlegung im Tooltip). */
const WertKachel: FC<KachelKontext> = ({ k, basis, monate }) => {
  const v = basis === 'vorjahr' ? k.total.veraenderung.vorjahr : k.total.veraenderung.vorher;
  const begriff = basisBegriff(basis);
  return (
    <Kachel
      wert={tEuro(k.total.value_sum)}
      label={TERMS.wertBeiBestellung.short}
      tip={TERMS.wertBeiBestellung.tip}
      sub={
        <>
          über 36 Monate, netto · Ø {fest(formatEur(k.total.value_per_anfo, 0))} je Anfo
          <Richtung
            wert={v.neu ? 'neu' : formatDelta(v.delta)}
            pfeil={v.neu ? null : richtung(v.delta)}
            grau={v.grau_grund !== null}
            etikett={`${begriff.short} (${basisFenster(k.vergleich, basis)})`}
            tip={`${veraenderungTip(v, k.grau_schwellen.veraenderung_anfos)} ${fensterSatz(k.vergleich, basis)} ${begriff.tip}`}
          />
          {monate === 3 && (
            <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
              kurzes Fenster: einzelne Aktionswochen wirken stark
            </Box>
          )}
        </>
      }
    />
  );
};

/**
 * Kachel „Bezahlquote" (D1): Quote der reifen Anfos, darunter der Unterschied in Prozentpunkten gegen die gewählte Basis.
 * ▲/▼ nur mit Test-Marke (Zwei-Anteile-Test, wie P2) — sonst ist der Unterschied nicht vom Zufall zu trennen.
 */
const QuoteKachel: FC<KachelKontext> = ({ k, basis }) => {
  const b = k.bezahlquote_vergleich?.[basis];
  const begriff = basisBegriff(basis);
  const flag = b?.test?.flag ?? null;
  return (
    <Kachel
      wert={formatPct(k.total.pay_rate)}
      label={`${TERMS.bezahlquote.label} (Anfos ab ${k.reife_tage} Tage alt)`}
      tip={`${TERMS.bezahlquote.tip} Wie sich die Bezahlquote über die Zeit entwickelt, zeigt „Umsatz & Bezahlqualität“.`}
      sub={
        <>
          {int(k.total.mature_paid)} von {int(k.total.mature_anfos)} haben gezahlt
          {b && b.delta_pp !== null && (
            <Richtung
              wert={ppText(b.delta_pp, 'lang')}
              pfeil={flag === 'up' ? '▲' : flag === 'down' ? '▼' : null}
              etikett={`gegen ${formatPct(b.pay_rate_basis)} (${reifBasisFenster(k.reif_vergleich, k.vergleich, basis)})`}
              tip={`${flag ? `${TERMS.abweichungZeitraumMarke.tip} ` : ''}${TERMS.bezahlquoteGegenVergleich.tip} ${reifSatz(k.reif_vergleich, basis, k.reife_tage)} Dort haben ${int(b.mature_paid_basis)} von ${int(b.mature_anfos_basis)} Anfos gezahlt. ${flag ? '' : 'Ohne ▲/▼: der Unterschied kann Zufall sein. '}${begriff.label}: ${fensterSatz(k.vergleich, basis)}`}
            />
          )}
        </>
      }
    />
  );
};

const Kanaele: FC = () => {
  const [monate, setMonate] = useState<KanalFensterMonate>(6);
  const [basis, setBasis] = useVergleichsbasis();
  const kanaele = useLadenFenster(fetchKanaele, monate);
  const flaechen = useLadenFenster(fetchWerbeflaechen, monate);
  // Direkt-Mailings letzte 3 Monate gegen die 3 davor — unabhängig vom Fenster, einmal je Seitenaufruf (Befunde D2/D3).
  const haelften = useLaden(fetchDmHaelften);
  const k = kanaele.data;
  // Bei 12 Monaten gilt das Vorjahr (E6). Nach der Fensterlänge, nicht nach gleichen Zeitstempeln: Der Vorzeitraum schließt
  // sekundengenau an und liegt einen Tag neben dem Vorjahr — der Vergleich der Grenzen sperrte nie (B49/B61/B73, 26.10.2026).
  const gleich = monate === 12;
  // Dann heißt es „Vorjahr"; die gespeicherte Wahl bleibt für die anderen Zeiträume erhalten.
  const wirksam: VergleichsBasis = gleich ? 'vorjahr' : basis;

  return (
    <ChapterPage
      title="Kanäle & Werbeflächen"
      question={KANAELE_FRAGE}
      intro={
        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          {TERMS.kanal.glossar} · {TERMS.werbeflaeche.glossar}
        </Typography>
      }
      standQuellen={[{ key: 'plx_orders', label: 'Bestellungen und Zahlungen (PL-X)' }]}
    >
      <Explainer title="Wie dieses Kapitel rechnet" sections={EXPLAINER} />
      <FensterWahl monate={monate} onChange={setMonate} />
      <BasisWahl basis={wirksam} onChange={setBasis} gleich={gleich} />
      {k && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
          <AnfosKachel k={k} basis={wirksam} monate={monate} />
          <WertKachel k={k} basis={wirksam} monate={monate} />
          <QuoteKachel k={k} basis={wirksam} monate={monate} />
        </Box>
      )}
      {/* Workshop-Lücke N4 (Plan 2026-10-29): Rückblick auf die letzte volle Woche, eigener Abruf, unabhängig vom Zeitraum oben.
          Direkt unter den Kacheln (Till 29.10.: der schnelle Wochenblick gehört nach oben, nicht ans Seitenende). */}
      <RueckblickKarte />
      {/* D2: der Rang über alle Flächen zuerst; Einladungen und Bestell-Links lesen dieselbe Antwort (E2-A). */}
      <RangKarte data={flaechen.data} fehler={flaechen.fehler} haelften={haelften.data} />
      <KanalKarte data={kanaele.data} fehler={kanaele.fehler} basis={wirksam} haelften={haelften.data} />
      <VerlaufKarte basis={basis} />
      <EinladungenKarte data={flaechen.data} fehler={flaechen.fehler} />
      <BestellLinksKarte data={flaechen.data} fehler={flaechen.fehler} />
      {/* D5: Wörterzeile ans Seitenende (Leitregel 6). */}
      <Woerterzeile />
    </ChapterPage>
  );
};

export default Kanaele;
